import React, { Suspense, useState } from 'react';
import { useTabContext } from 'GlobalState/Context/TabContext';
import { useParams } from 'react-router-dom';
import { useGlobalContext } from 'GlobalState/Context/GlobalContext';

// Table
import TableSingleProject from 'components/Tables/TableSingleProject/TableSingleProject';
import SkeletonTable from 'components/LoadingSkeletons/SkeletonTable/SkeletonTable';

// UI components
import Card from 'components/Card/Card';
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary';

// Styles
import tabProjectStyles from './Project.module.scss';
import sharedStyles from 'styles/shared.module.scss';
import layoutStyles from 'styles/layout.module.scss';

// Icons
import ExpandSolid from 'components/Icons/expand-solid';
import CollapseSolid from 'components/Icons/collapse-solid';
import BillingReportFilter from 'components/Form/Selects/BillingReportFilter';
import SkeletonCustomField from 'components/LoadingSkeletons/SkeletonCustomField/SkeletonCustomField';

import { useQuery } from 'react-query';
import { Tooltip } from 'react-tooltip';
import instance from 'axiosInstance';

const requestCSV = async ({ queryKey }) => {
  const response = await instance.get(
    `/private/admin/projects-exports/${queryKey[3]}?type=${queryKey[1]}&period=${queryKey[2]}`
  );

  return response;
};

const requestComponentBreakdownCSV = async ({ queryKey }) => {
  const response = await instance.get(
    `/private/admin/component-breakdown-export/${queryKey[1]}?period=${queryKey[2]}`
  );

  return response;
};

const requestComponentBreakdownClipboard = async ({ queryKey }) => {
  const response = await instance.get(
    `/private/admin/component-breakdown-export/${queryKey[1]}?period=${queryKey[2]}&format=clipboard`
  );

  return response;
};

const requestEmployeeComponentBreakdownCSV = async ({ queryKey }) => {
  const response = await instance.get(
    `/private/admin/employee-component-breakdown-export/${queryKey[1]}?period=${queryKey[2]}`
  );

  return response;
};

const requestProjectComponents = async ({ queryKey }) => {
  const response = await instance.get(
    `/private/project-components?project=${queryKey[1]}&period=${queryKey[2]}`
  );

  return response.data;
};

const requestProjectInformation = async ({ queryKey }) => {
  const response = await instance.get(`/private/projects/${queryKey[1]}`);

  return response.data;
};

const requestProjects = async ({ queryKey }) => {
  const response = await instance.get(
    `/private/projects?period=${queryKey[1]}`
  );

  return response.data;
};

const ProjectTab = () => {
  const {
    state: { isTableExpanded, billingPeriod, isTableSumMode },
    dispatch,
  } = useTabContext();

  const {
    globalState: { isAdmin },
  } = useGlobalContext();

  const { slug } = useParams();
  const [exportType, setExportType] = useState('full');
  const [showLegacyExports, setShowLegacyExports] = useState(false);
  const { data: projectComponents, isLoading: isProjectComponentsLoading } =
    useQuery(
      ['project_components', slug, billingPeriod.value],
      requestProjectComponents,
      {
        suspense: false,
        keepPreviousData: true,
      }
    );
  const { data: projectInformation, isLoading: isProjectInformationLoading } =
    useQuery(['projects', slug], requestProjectInformation, {
      suspense: false,
      keepPreviousData: true,
    });
  const { data: projectsForPeriod, isLoading: isProjectsForPeriodLoading } =
    useQuery(['projects', billingPeriod.value], requestProjects, {
      suspense: false,
      keepPreviousData: true,
    });
  const projectTotalHours =
    projectComponents?.reduce(
      (previousHours, currentComponent) =>
        previousHours + currentComponent.totalTime,
      0
    ) ?? 0;
  const projectRetainerHours = projectInformation?.[0]?.retainer;
  const projectOverviewDataForPeriod = projectsForPeriod?.find(
    (projectRow) => projectRow?.project?.slug === slug
  );
  const daysToBilling = projectOverviewDataForPeriod?.billing;
  const periodTimeLeftLabel =
    isProjectsForPeriodLoading || daysToBilling === undefined
      ? '...'
      : `${daysToBilling} days left from retainer period`;
  const billableSummaryLabel = `${
    isProjectComponentsLoading ? '...' : `${projectTotalHours}h`
  } / ${
    isProjectInformationLoading
      ? '...'
      : projectRetainerHours !== undefined
      ? `${projectRetainerHours}h`
      : '—'
  } (${periodTimeLeftLabel})`;

  const { refetch } = useQuery(
    ['csv', exportType, billingPeriod.value, slug],
    requestCSV,
    {
      enabled: false,
      suspense: false,
      onSuccess: (response) => {
        const fileName = response.headers['project-details'];
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('href', url);
        a.setAttribute('download', fileName + '.csv');
        a.click();
      },
    }
  );

  const { refetch: refetchComponentBreakdown } = useQuery(
    ['component-breakdown-csv', slug, billingPeriod.value],
    requestComponentBreakdownCSV,
    {
      enabled: false,
      suspense: false,
      onSuccess: (response) => {
        const fileName = response.headers['project-details'];
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('href', url);
        a.setAttribute('download', fileName + '.csv');
        a.click();
      },
    }
  );

  const { refetch: refetchComponentBreakdownClipboard } = useQuery(
    ['component-breakdown-clipboard', slug, billingPeriod.value],
    requestComponentBreakdownClipboard,
    {
      enabled: false,
      suspense: false,
      onSuccess: (response) => {
        if (response.data?.data) {
          navigator.clipboard.writeText(response.data.data);
        }
      },
    }
  );

  const { refetch: refetchEmployeeComponentBreakdown } = useQuery(
    ['employee-component-breakdown-csv', slug, billingPeriod.value],
    requestEmployeeComponentBreakdownCSV,
    {
      enabled: false,
      suspense: false,
      onSuccess: (response) => {
        const fileName = response.headers['project-details'];
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('href', url);
        a.setAttribute('download', fileName + '.csv');
        a.click();
      },
    }
  );

  return (
    <>
      <Card
        leftAdditional={
          <span className={tabProjectStyles['report-billable-row']}>
            <ErrorBoundary>
              <Suspense
                fallback={
                  <SkeletonCustomField
                    width="200px"
                    height="40px"
                    backgroundColor="var(--field-skeleton-background-color)"
                    backgroundShineColor="var(--field-skeleton-shine-background-color)"
                    borderRadius="5px"
                  />
                }
              >
                <span className={tabProjectStyles['report-billable-filter']}>
                  <BillingReportFilter />
                </span>
              </Suspense>
            </ErrorBoundary>
            <span className={tabProjectStyles['billable-hours-separator']}>
              |
            </span>
            <span className={tabProjectStyles['billable-hours-summary']}>
              {billableSummaryLabel}
            </span>
          </span>
        }
        headerAdditional={
          <div className={tabProjectStyles['report-header-actions']}>
            <div className={tabProjectStyles['report-header-actions-row']}>
              <div
                className={`${layoutStyles['row-wrap']} ${tabProjectStyles['report-header-primary-actions']}`}
              >
                <span
                  className={sharedStyles['action']}
                  onClick={() => {
                    dispatch({ type: 'setIsTableExpanded', payload: false });
                    dispatch({ type: 'toggleIsTableSumMode' });
                  }}
                  data-tooltip-id="project-tooltip"
                  data-tooltip-content="Toggle between detailed view and component summary view"
                >
                  <span>
                    {isTableSumMode ? 'Expand mode' : 'Component Sum Mode'}
                  </span>
                </span>

                {!isTableExpanded ? (
                  <span
                    className={sharedStyles['action']}
                    onClick={() =>
                      dispatch({ type: 'setIsTableExpanded', payload: true })
                    }
                    data-tooltip-id="project-tooltip"
                    data-tooltip-content="Expand all component rows to show details"
                  >
                    <span>Expand all</span>
                  </span>
                ) : (
                  <span
                    className={sharedStyles['action']}
                    onClick={() =>
                      dispatch({ type: 'setIsTableExpanded', payload: false })
                    }
                    data-tooltip-id="project-tooltip"
                    data-tooltip-content="Collapse all component rows"
                  >
                    <span>Collapse all</span>
                  </span>
                )}
              </div>
              {isAdmin && (
                <div
                  className={`${layoutStyles['row-wrap']} ${tabProjectStyles['report-header-admin-actions']}`}
                >
                  <span
                    className={`${sharedStyles['action']} ${tabProjectStyles['report-header-more-actions-toggle']}`}
                    onClick={() => setShowLegacyExports(!showLegacyExports)}
                    data-tooltip-id="project-tooltip"
                    data-tooltip-content={
                      showLegacyExports
                        ? 'Hide export options'
                        : 'Show more export options'
                    }
                  >
                    <span
                      className={
                        tabProjectStyles['report-header-more-actions-label']
                      }
                    >
                      {showLegacyExports
                        ? 'Hide export options'
                        : 'Show more export options'}
                      {showLegacyExports ? (
                        <CollapseSolid width="14" height="14" />
                      ) : (
                        <ExpandSolid width="14" height="14" />
                      )}
                    </span>
                  </span>
                </div>
              )}
            </div>
            {isAdmin && showLegacyExports && (
              <div
                className={`${layoutStyles['row-wrap']} ${tabProjectStyles['report-header-legacy-actions']}`}
              >
                <span
                  className={sharedStyles['action']}
                  onClick={() => {
                    setTimeout(() => {
                      refetchComponentBreakdown();
                    }, 100);
                  }}
                  data-tooltip-id="project-tooltip"
                  data-tooltip-content="Download CSV with component breakdown and tasks"
                >
                  <span>Export Component Breakdown</span>
                </span>
                <span
                  className={sharedStyles['action']}
                  onClick={() => {
                    setTimeout(() => {
                      refetchComponentBreakdownClipboard();
                    }, 100);
                  }}
                  data-tooltip-id="project-tooltip"
                  data-tooltip-content="Copy component breakdown to clipboard"
                >
                  <span>Copy Component Breakdown</span>
                </span>
                <span
                  className={sharedStyles['action']}
                  onClick={() => {
                    setExportType('full');
                    setTimeout(() => {
                      refetch();
                    }, 100);
                  }}
                  data-tooltip-id="project-tooltip"
                  data-tooltip-content="Export raw time entries without normalization"
                >
                  <span>Export Raw</span>
                </span>
                <span
                  className={sharedStyles['action']}
                  onClick={() => {
                    setExportType('normalized');
                    setTimeout(() => {
                      refetch();
                    }, 100);
                  }}
                  data-tooltip-id="project-tooltip"
                  data-tooltip-content="Export time entries with multiplier normalization applied"
                >
                  <span>Export Normalized</span>
                </span>
                <span
                  className={sharedStyles['action']}
                  onClick={() => {
                    setTimeout(() => {
                      refetchEmployeeComponentBreakdown();
                    }, 100);
                  }}
                  data-tooltip-id="project-tooltip"
                  data-tooltip-content="Export breakdown grouped by employee and component"
                >
                  <span>Export Employee Breakdown</span>
                </span>
              </div>
            )}
          </div>
        }
      >
        <ErrorBoundary>
          <Suspense fallback={<SkeletonTable countTableBodyRows={10} />}>
            <TableSingleProject />
          </Suspense>
        </ErrorBoundary>
      </Card>
      <Tooltip id="project-tooltip" />
    </>
  );
};

export default ProjectTab;
